import {
  Product,
  Collection,
  Cart,
  ShopifyProduct,
  ShopifyCollection,
  ShopifyCart,
  ShopifyResponse,
  ProductsResponse,
  ProductResponse,
  CollectionsResponse,
  CollectionResponse,
  CartResponse,
  CartCreateResponse,
  CartLinesAddResponse,
  CartLinesUpdateResponse,
  CartLinesRemoveResponse,
  CartItem,
  Money,
  ShopifyArticle,
  ArticlesResponse,
  Image as ShopifyImage,
} from './types';
import { cache } from 'react';

const domain = process.env.SHOPIFY_STORE_DOMAIN!;
const storefrontAccessToken = process.env.SHOPIFY_STOREFRONT_ACCESS_TOKEN!;
const adminAccessToken = process.env.SHOPIFY_ADMIN_ACCESS_TOKEN;
const apiVersion = '2024-01';

const endpoint = `https://${domain}/api/${apiVersion}/graphql.json`;
const adminEndpoint = `https://${domain}/admin/api/${apiVersion}/graphql.json`;

type ShopifyGraphQLError = {
  message: string;
  locations?: { line: number; column: number }[];
  path?: string[];
  extensions?: {
    code?: string;
    requiredAccess?: string;
    [key: string]: unknown;
  };
};

class ShopifyGraphQLErrorResponse extends Error {
  errors: ShopifyGraphQLError[];

  constructor(errors: ShopifyGraphQLError[]) {
    super(errors[0]?.message || 'Shopify GraphQL error');
    this.name = 'ShopifyGraphQLErrorResponse';
    this.errors = errors;
  }
}

function isShopifyAccessDeniedError(error: unknown): boolean {
  return (
    error instanceof ShopifyGraphQLErrorResponse &&
    error.errors.some((e) => e.extensions?.code === 'ACCESS_DENIED' || e.message.includes('Access denied'))
  );
}

// Sentinel for withAdminCatalogFallback: when no emptyValue is supplied, a
// failing fallback rethrows instead of masking the failure with an empty
// result. Single-entity lookups (getProduct, getCollection) must use this —
// returning null on a throttled/failed admin call made pages notFound() live
// products, serving crawlers 404s and "Product Not Found" titles.
const RETHROW: unique symbol = Symbol('rethrow');

// Short-TTL memo for Admin API fallback lookups. The fallback rides a
// rate-limited cost bucket, so a crawler burst re-fetching the same catalog
// data dozens of times just throttles itself to death. Sharing the in-flight
// promise collapses concurrent identical lookups into one call; the TTL keeps
// the degraded path from hammering Shopify. Deliberately NOT applied to the
// Storefront path — once its token scopes are fixed, reads stay fresh.
const ADMIN_CATALOG_TTL_MS = 2 * 60 * 1000;
const adminCatalogMemo = new Map<string, { promise: Promise<unknown>; expiresAt: number }>();

function adminMemo<T>(key: string, load: () => Promise<T>): Promise<T> {
  const hit = adminCatalogMemo.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.promise as Promise<T>;
  if (adminCatalogMemo.size > 500) adminCatalogMemo.clear();
  const promise = load();
  adminCatalogMemo.set(key, { promise, expiresAt: Date.now() + ADMIN_CATALOG_TTL_MS });
  promise.catch(() => {
    // Don't cache failures — let the next request retry fresh.
    if (adminCatalogMemo.get(key)?.promise === promise) adminCatalogMemo.delete(key);
  });
  return promise;
}

async function withAdminCatalogFallback<T>(
  error: unknown,
  operation: string,
  fallback: () => Promise<T>,
  emptyValue: T | typeof RETHROW = RETHROW,
): Promise<T> {
  if (!isShopifyAccessDeniedError(error)) throw error;

  try {
    return await fallback();
  } catch (fallbackError) {
    console.error(`[shopify] Admin API fallback failed for ${operation}:`, fallbackError);
    if (emptyValue === RETHROW) throw fallbackError;
    return emptyValue;
  }
}

interface AdminProductVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  selectedOptions: {
    name: string;
    value: string;
  }[];
  price: string;
  compareAtPrice: string | null;
  image: ShopifyImage | null;
}

interface AdminProduct {
  id: string;
  handle: string;
  title: string;
  description: string;
  descriptionHtml: string;
  status: string;
  featuredImage: ShopifyImage | null;
  images: {
    edges: {
      node: ShopifyImage;
    }[];
  };
  options: {
    id: string;
    name: string;
    values: string[];
  }[];
  priceRangeV2: {
    minVariantPrice: Money;
    maxVariantPrice: Money;
  };
  variants: {
    edges: {
      node: AdminProductVariant;
    }[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
  };
  tags: string[];
  productType: string;
  vendor: string;
  seo: {
    title: string | null;
    description: string | null;
  };
}

interface AdminCollection {
  id: string;
  handle: string;
  title: string;
  description: string;
  image: ShopifyImage | null;
  products?: {
    edges: {
      node: AdminProduct;
    }[];
  };
  seo: {
    title: string | null;
    description: string | null;
  };
}

interface AdminArticle {
  handle: string;
  title: string;
  body: string;
  publishedAt: string;
  image: ShopifyImage | null;
}

interface AdminProductsResponse {
  products: {
    edges: { node: AdminProduct }[];
  };
}

interface AdminProductsPageResponse {
  products: {
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
    edges: { node: AdminProduct }[];
  };
}

interface AdminCollectionsResponse {
  collections: {
    edges: { node: AdminCollection }[];
  };
}

interface AdminCartVariant {
  id: string;
  title: string;
  selectedOptions: {
    name: string;
    value: string;
  }[];
  price: string;
  product: {
    id: string;
    handle: string;
    title: string;
    featuredImage: ShopifyImage | null;
  } | null;
}

interface AdminCartVariantsResponse {
  nodes: (AdminCartVariant | null)[];
}

const ADMIN_VARIANT_FIELDS = `
  id
  title
  availableForSale
  selectedOptions {
    name
    value
  }
  price
  compareAtPrice
  image {
    url
    altText
    width
    height
  }
`;

const ADMIN_PRODUCT_FIELDS = `
  id
  handle
  title
  description
  descriptionHtml
  status
  featuredImage {
    url
    altText
    width
    height
  }
  images(first: 10) {
    edges {
      node {
        url
        altText
        width
        height
      }
    }
  }
  options {
    id
    name
    values
  }
  priceRangeV2 {
    minVariantPrice {
      amount
      currencyCode
    }
    maxVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: 100) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        ${ADMIN_VARIANT_FIELDS}
      }
    }
  }
  tags
  productType
  vendor
  seo {
    title
    description
  }
`;

const ADMIN_LIST_PRODUCT_FIELDS = ADMIN_PRODUCT_FIELDS.replace('variants(first: 100)', 'variants(first: 1)');

// GraphQL Fragments
const PRODUCT_FRAGMENT = `
  fragment ProductFragment on Product {
    id
    handle
    title
    description
    descriptionHtml
    availableForSale
    featuredImage {
      url
      altText
      width
      height
    }
    images(first: 10) {
      edges {
        node {
          url
          altText
          width
          height
        }
      }
    }
    options {
      id
      name
      values
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
      maxVariantPrice {
        amount
        currencyCode
      }
    }
    variants(first: 100) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          availableForSale
          selectedOptions {
            name
            value
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          image {
            url
            altText
            width
            height
          }
        }
      }
    }
    tags
    productType
    vendor
    seo {
      title
      description
    }
  }
`;

const COLLECTION_FRAGMENT = `
  fragment CollectionFragment on Collection {
    id
    handle
    title
    description
    image {
      url
      altText
      width
      height
    }
    seo {
      title
      description
    }
  }
`;

// Reusable line-shape sub-fragment so both top-level cart lines AND merged
// `lineComponents` (from a Shopify Cart Transform Function) return identical
// shapes. Keeps reshapeCart's flatten step trivial.
const CART_LINE_FIELDS = `
  id
  quantity
  attributes {
    key
    value
  }
  cost {
    totalAmount {
      amount
      currencyCode
    }
  }
  merchandise {
    ... on ProductVariant {
      id
      title
      price {
        amount
        currencyCode
      }
    }
  }
`;

const CART_FRAGMENT = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    discountCodes {
      code
      applicable
    }
    cost {
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
      totalTaxAmount {
        amount
        currencyCode
      }
    }
    lines(first: 100) {
      edges {
        node {
          ${CART_LINE_FIELDS}
          # If a Cart Transform Function has merged this line into a bundle,
          # the original (componentised) lines live here. Empty/null for
          # regular un-merged lines, so this is safe to query unconditionally.
          ... on ComponentizableCartLine {
            lineComponents {
              ${CART_LINE_FIELDS}
            }
          }
        }
      }
    }
    totalQuantity
  }
`;

// Fetch helper
async function shopifyFetch<T>({
  query,
  variables,
  cache = 'no-store',
  tags,
}: {
  query: string;
  variables?: Record<string, unknown>;
  cache?: RequestCache;
  tags?: string[];
}): Promise<T> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
    cache,
    ...(tags && { next: { tags } }),
  });

  if (!response.ok) {
    throw new Error(`Shopify API error: ${response.status} ${response.statusText}`);
  }

  const json: ShopifyResponse<T> = await response.json();

  if (json.errors) {
    const errors = json.errors as ShopifyGraphQLError[];
    const isAccessDenied = errors.some((e) => e.extensions?.code === 'ACCESS_DENIED');
    if (!isAccessDenied) {
      console.error('Shopify GraphQL Errors:', errors);
    }
    throw new ShopifyGraphQLErrorResponse(errors);
  }

  return json.data;
}

const ADMIN_FETCH_MAX_ATTEMPTS = 6;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// The Admin API cost bucket is shared per shop, so a crawler burst rendering
// many pages at once just makes dozens of parallel lookups thrash the bucket
// and starve each other's retries. Cap in-process concurrency; queued calls
// inherit the released slot.
const ADMIN_FETCH_MAX_CONCURRENT = 4;
let adminFetchActive = 0;
const adminFetchWaiters: (() => void)[] = [];

async function acquireAdminSlot(): Promise<void> {
  if (adminFetchActive < ADMIN_FETCH_MAX_CONCURRENT) {
    adminFetchActive++;
    return;
  }
  await new Promise<void>((resolve) => adminFetchWaiters.push(resolve));
}

function releaseAdminSlot(): void {
  const next = adminFetchWaiters.shift();
  if (next) {
    next(); // hand the slot over; adminFetchActive stays the same
  } else {
    adminFetchActive--;
  }
}

async function shopifyAdminFetch<T>({
  query,
  variables,
}: {
  query: string;
  variables?: Record<string, unknown>;
}): Promise<T> {
  await acquireAdminSlot();
  try {
    return await shopifyAdminFetchInner<T>({ query, variables });
  } finally {
    releaseAdminSlot();
  }
}

async function shopifyAdminFetchInner<T>({
  query,
  variables,
}: {
  query: string;
  variables?: Record<string, unknown>;
}): Promise<T> {
  if (!adminAccessToken) {
    throw new Error('SHOPIFY_ADMIN_ACCESS_TOKEN is not configured');
  }

  // While the Storefront token lacks read scopes, the Admin API carries the
  // whole catalog. Its cost bucket drains fast under crawler bursts
  // (Googlebot, SEO tools), so retry throttled calls before failing — a
  // surfaced failure upstream reads as "product not found".
  for (let attempt = 1; ; attempt++) {
    const response = await fetch(adminEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Access-Token': adminAccessToken,
      },
      body: JSON.stringify({ query, variables }),
      cache: 'no-store',
    });

    if (response.status === 429 && attempt < ADMIN_FETCH_MAX_ATTEMPTS) {
      const retryAfter = Number(response.headers.get('Retry-After'));
      const baseMs = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 1000;
      await sleep(Math.min(baseMs, 8000) + Math.random() * 250);
      continue;
    }

    if (!response.ok) {
      throw new Error(`Shopify Admin API error: ${response.status} ${response.statusText}`);
    }

    const json: ShopifyResponse<T> & {
      extensions?: {
        cost?: {
          requestedQueryCost?: number;
          throttleStatus?: { currentlyAvailable?: number; restoreRate?: number };
        };
      };
    } = await response.json();

    if (json.errors) {
      const errors = json.errors as ShopifyGraphQLError[];
      const throttled = errors.some((e) => e.extensions?.code === 'THROTTLED');
      if (throttled && attempt < ADMIN_FETCH_MAX_ATTEMPTS) {
        // Cost-based throttle: wait roughly until the bucket has restored
        // enough points for this query, then retry.
        const cost = json.extensions?.cost;
        const needed =
          (cost?.requestedQueryCost ?? 50) - (cost?.throttleStatus?.currentlyAvailable ?? 0);
        const restoreRate = cost?.throttleStatus?.restoreRate || 50;
        const waitMs = Math.min(Math.max((needed / restoreRate) * 1000, 500), 8000);
        await sleep(waitMs + Math.random() * 250);
        continue;
      }
      console.error('Shopify Admin GraphQL Errors:', errors);
      throw new ShopifyGraphQLErrorResponse(errors);
    }

    return json.data;
  }
}

// Reshape functions
function reshapeProduct(product: ShopifyProduct): Product {
  return {
    ...product,
    images: product.images.edges.map((edge) => edge.node),
    variants: product.variants.edges.map((edge) => edge.node),
  };
}

function reshapeCollection(collection: ShopifyCollection): Collection {
  return {
    ...collection,
    products: collection.products?.edges.map((edge) => reshapeProduct(edge.node)),
  };
}

function reshapeCart(cart: ShopifyCart): Cart {
  // Defensive: drop any line that came back without an id (or with an empty
  // string id). These slip through under certain Shopify error conditions
  // and break two things — duplicate empty React keys in the cart drawer,
  // and 500s when the customer tries to delete them (we'd send `['']` to
  // cartLinesRemove which Shopify rejects).
  const rawLines = cart.lines.edges
    .map((edge) => edge.node)
    .filter((node) => {
      if (!node?.id) {
        if (typeof console !== 'undefined') {
          console.warn('[reshapeCart] dropping cart line with empty id', node);
        }
        return false;
      }
      return true;
    });

  // Flatten any Cart Transform merged bundles. When a Cart Transform Function
  // is active, Shopify replaces matching cart lines with a single bundle line
  // (ComponentizableCartLine) and tucks the original lines into
  // `lineComponents`. The cart drawer + dedup logic + cascade rules expect
  // the original lines, so we unwrap them here. Backward-compatible: if no
  // function is active (or a line has no components), it's returned as-is.
  const lines = rawLines.flatMap((node) => {
    const components = (node as { lineComponents?: unknown }).lineComponents;
    if (Array.isArray(components) && components.length > 0) {
      return components.filter(
        (c): c is typeof node => !!(c as { id?: string })?.id
      );
    }
    return [node];
  }).map(normaliseCartLine);

  return { ...cart, discountCodes: cart.discountCodes || [], lines };
}

function normaliseCartLine(line: CartItem): CartItem {
  const merchandise = line.merchandise as CartItem['merchandise'] & {
    selectedOptions?: CartItem['merchandise']['selectedOptions'];
    product?: CartItem['merchandise']['product'];
    price?: Money;
  };
  const fallbackTitle = merchandise.title || 'Product';
  const currencyCode =
    merchandise.price?.currencyCode ||
    line.cost?.totalAmount?.currencyCode ||
    'GBP';

  return {
    ...line,
    attributes: line.attributes || [],
    merchandise: {
      ...merchandise,
      selectedOptions: merchandise.selectedOptions || [],
      product: merchandise.product || {
        id: '',
        handle: '',
        title: fallbackTitle,
        featuredImage: null,
      },
      price: merchandise.price || {
        amount: '0',
        currencyCode,
      },
    },
  };
}

function normaliseImage(image: ShopifyImage | null | undefined): ShopifyImage | null {
  if (!image?.url) return null;

  return {
    url: image.url,
    altText: image.altText || null,
    width: image.width,
    height: image.height,
  };
}

function toMoney(amount: string | number | null | undefined, currencyCode: string): Money {
  return {
    amount: amount == null ? '0' : String(amount),
    currencyCode,
  };
}

async function adminGetCartVariants(variantIds: string[]): Promise<Map<string, AdminCartVariant>> {
  const query = `
    query AdminGetCartVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          selectedOptions {
            name
            value
          }
          price
          product {
            id
            handle
            title
            featuredImage {
              url
              altText
              width
              height
            }
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<AdminCartVariantsResponse>({
    query,
    variables: { ids: variantIds },
  });

  return new Map(
    data.nodes
      .filter((variant): variant is AdminCartVariant => Boolean(variant?.id))
      .map((variant) => [variant.id, variant])
  );
}

async function enrichCart(cart: Cart): Promise<Cart> {
  const variantIds = Array.from(
    new Set(
      cart.lines
        .map((line) => line.merchandise.id)
        .filter((id): id is string => Boolean(id))
    )
  );

  if (variantIds.length === 0) return cart;

  try {
    const variantsById = await adminGetCartVariants(variantIds);
    if (variantsById.size === 0) return cart;

    return {
      ...cart,
      lines: cart.lines.map((line) => {
        const adminVariant = variantsById.get(line.merchandise.id);
        if (!adminVariant) return line;

        const product = adminVariant.product;
        const currencyCode =
          line.merchandise.price.currencyCode ||
          line.cost.totalAmount.currencyCode ||
          'GBP';

        return {
          ...line,
          merchandise: {
            ...line.merchandise,
            title: adminVariant.title || line.merchandise.title,
            selectedOptions: adminVariant.selectedOptions || line.merchandise.selectedOptions,
            price: line.merchandise.price || toMoney(adminVariant.price, currencyCode),
            product: {
              id: product?.id || line.merchandise.product.id,
              handle: product?.handle || line.merchandise.product.handle,
              title: product?.title || line.merchandise.product.title || line.merchandise.title,
              featuredImage:
                normaliseImage(product?.featuredImage) ||
                line.merchandise.product.featuredImage ||
                null,
            },
          },
        };
      }),
    };
  } catch (error) {
    console.error('[shopify] Admin cart enrichment failed:', error);
    return cart;
  }
}

function adminProductToShopifyProduct(product: AdminProduct): ShopifyProduct | null {
  if (product.status !== 'ACTIVE') return null;

  const currencyCode = product.priceRangeV2.minVariantPrice.currencyCode;
  const imageEdges = (product.images?.edges || [])
    .map((edge) => normaliseImage(edge.node))
    .filter((image): image is ShopifyImage => Boolean(image));
  const featuredImage = normaliseImage(product.featuredImage) || imageEdges[0] || null;
  const images = imageEdges.length > 0 ? imageEdges : featuredImage ? [featuredImage] : [];

  return {
    id: product.id,
    handle: product.handle,
    title: product.title,
    description: product.description || '',
    descriptionHtml: product.descriptionHtml || '',
    availableForSale: product.variants.edges.some((edge) => edge.node.availableForSale),
    featuredImage,
    images: {
      edges: images.map((image) => ({ node: image })),
    },
    options: product.options || [],
    priceRange: product.priceRangeV2,
    variants: {
      pageInfo: product.variants.pageInfo,
      edges: product.variants.edges.map((edge) => ({
        node: {
          id: edge.node.id,
          title: edge.node.title,
          availableForSale: edge.node.availableForSale,
          selectedOptions: edge.node.selectedOptions || [],
          price: toMoney(edge.node.price, currencyCode),
          compareAtPrice: edge.node.compareAtPrice
            ? toMoney(edge.node.compareAtPrice, currencyCode)
            : null,
          image: normaliseImage(edge.node.image) || undefined,
        },
      })),
    },
    tags: product.tags || [],
    productType: product.productType || '',
    vendor: product.vendor || '',
    seo: product.seo || { title: null, description: null },
  };
}

function adminProductToProduct(product: AdminProduct): Product | null {
  const shopifyProduct = adminProductToShopifyProduct(product);
  return shopifyProduct ? reshapeProduct(shopifyProduct) : null;
}

function adminCollectionToCollection(collection: AdminCollection): Collection {
  const products = collection.products?.edges
    .map((edge) => adminProductToProduct(edge.node))
    .filter((product): product is Product => Boolean(product))
    .filter((product) => !isFeeProductHandle(product.handle));

  return {
    id: collection.id,
    handle: collection.handle,
    title: collection.title,
    description: collection.description || '',
    image: normaliseImage(collection.image),
    products,
    seo: collection.seo || { title: null, description: null },
  };
}

async function adminGetProducts(first: number = 20): Promise<Product[]> {
  const query = `
    query AdminGetProducts($first: Int!) {
      products(first: $first, query: "status:active") {
        edges {
          node {
            ${ADMIN_LIST_PRODUCT_FIELDS}
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<AdminProductsResponse>({
    query,
    variables: { first },
  });

  return data.products.edges
    .map((edge) => adminProductToProduct(edge.node))
    .filter((product): product is Product => Boolean(product))
    .filter((product) => !isFeeProductHandle(product.handle));
}

async function adminGetAllProducts(): Promise<Product[]> {
  const query = `
    query AdminGetAllProducts($first: Int!, $after: String) {
      products(first: $first, after: $after, query: "status:active") {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ${ADMIN_LIST_PRODUCT_FIELDS}
          }
        }
      }
    }
  `;

  const products: Product[] = [];
  let after: string | null = null;

  do {
    const data: AdminProductsPageResponse = await shopifyAdminFetch<AdminProductsPageResponse>({
      query,
      variables: { first: 250, after },
    });

    products.push(
      ...data.products.edges
        .map((edge) => adminProductToProduct(edge.node))
        .filter((product): product is Product => Boolean(product))
        .filter((product) => !isFeeProductHandle(product.handle))
    );

    after = data.products.pageInfo.hasNextPage ? data.products.pageInfo.endCursor : null;
  } while (after);

  return products;
}

async function adminGetMoreVariants(
  handle: string,
  cursor: string,
): Promise<AdminProduct['variants'] | null> {
  const query = `
    query AdminGetMoreVariants($handle: String!, $cursor: String!) {
      productByHandle(handle: $handle) {
        variants(first: 250, after: $cursor) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ${ADMIN_VARIANT_FIELDS}
            }
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<{
    productByHandle: { variants: AdminProduct['variants'] } | null;
  }>({
    query,
    variables: { handle, cursor },
  });

  return data.productByHandle?.variants || null;
}

async function adminGetProduct(handle: string): Promise<Product | null> {
  const query = `
    query AdminGetProduct($handle: String!) {
      productByHandle(handle: $handle) {
        ${ADMIN_PRODUCT_FIELDS}
      }
    }
  `;

  const data = await shopifyAdminFetch<{
    productByHandle: AdminProduct | null;
  }>({
    query,
    variables: { handle },
  });

  if (!data.productByHandle) return null;

  // Match Storefront semantics: draft/archived products don't exist publicly.
  // The admin list queries filter status:active; the single lookup must too.
  if (data.productByHandle.status !== 'ACTIVE') return null;

  let pageInfo = data.productByHandle.variants.pageInfo;
  while (pageInfo.hasNextPage && pageInfo.endCursor) {
    const more = await adminGetMoreVariants(handle, pageInfo.endCursor);
    if (!more) break;
    data.productByHandle.variants.edges.push(...more.edges);
    pageInfo = more.pageInfo;
  }

  return adminProductToProduct(data.productByHandle);
}

async function adminSearchProducts(searchQuery: string, first: number = 50): Promise<Product[]> {
  const query = `
    query AdminSearchProducts($query: String!, $first: Int!) {
      products(first: $first, query: $query) {
        edges {
          node {
            ${ADMIN_LIST_PRODUCT_FIELDS}
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<AdminProductsResponse>({
    query,
    variables: { query: searchQuery, first },
  });

  return data.products.edges
    .map((edge) => adminProductToProduct(edge.node))
    .filter((product): product is Product => Boolean(product))
    .filter((product) => !isFeeProductHandle(product.handle));
}

async function adminGetCollections(): Promise<Collection[]> {
  const query = `
    query AdminGetCollections {
      collections(first: 100) {
        edges {
          node {
            id
            handle
            title
            description
            image {
              url
              altText
              width
              height
            }
            seo {
              title
              description
            }
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<AdminCollectionsResponse>({ query });

  return data.collections.edges.map((edge) => adminCollectionToCollection(edge.node));
}

async function adminGetCollection(handle: string): Promise<Collection | null> {
  const query = `
    query AdminGetCollection($handle: String!) {
      collectionByHandle(handle: $handle) {
        id
        handle
        title
        description
        image {
          url
          altText
          width
          height
        }
        seo {
          title
          description
        }
        products(first: 100) {
          edges {
            node {
              ${ADMIN_LIST_PRODUCT_FIELDS}
            }
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<{
    collectionByHandle: AdminCollection | null;
  }>({
    query,
    variables: { handle },
  });

  return data.collectionByHandle ? adminCollectionToCollection(data.collectionByHandle) : null;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

async function adminGetShopifyArticles(): Promise<ShopifyArticle[]> {
  const query = `
    query AdminGetArticles {
      articles(first: 250) {
        edges {
          node {
            handle
            title
            body
            publishedAt
            image {
              url
              altText
              width
              height
            }
          }
        }
      }
    }
  `;

  const data = await shopifyAdminFetch<{
    articles: { edges: { node: AdminArticle }[] };
  }>({ query });

  return data.articles.edges.map((edge) => {
    const excerpt = stripHtml(edge.node.body).slice(0, 200);
    return {
      handle: edge.node.handle,
      title: edge.node.title,
      excerpt,
      excerptHtml: null,
      contentHtml: edge.node.body,
      publishedAt: edge.node.publishedAt,
      image: normaliseImage(edge.node.image),
      seo: {
        title: edge.node.title,
        description: excerpt,
      },
    };
  });
}

// Product APIs
// Internal helper: handles for products that exist in Shopify only to back the
// digitising-fee cart line. They must remain fetchable by handle (the product
// page loads them to populate the DigitisingFeeSelector) but should never
// appear in customer-facing listings, sitemaps, or related-products grids.
const FEE_PRODUCT_HANDLES = new Set<string>([
  'embroidery-digitising-fee-front',
  'embroidery-digitising-fee-back',
]);

export function isFeeProductHandle(handle: string): boolean {
  return FEE_PRODUCT_HANDLES.has(handle);
}

export const getProducts = cache(async function getProducts(
  first: number = 20,
): Promise<Product[]> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query GetProducts($first: Int!) {
      products(first: $first) {
        edges {
          node {
            ...ProductFragment
          }
        }
      }
    }
  `;

  try {
    const data = await shopifyFetch<ProductsResponse>({
      query,
      variables: { first },
      tags: ['products'],
    });

    return data.products.edges
      .map((edge) => reshapeProduct(edge.node))
      .filter((p) => !isFeeProductHandle(p.handle));
  } catch (error) {
    return withAdminCatalogFallback(error, 'getProducts', () => adminMemo(`adminGetProducts:${first}`, () => adminGetProducts(first)), []);
  }
});

export async function getAllProducts(): Promise<Product[]> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query GetAllProducts($first: Int!, $after: String) {
      products(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...ProductFragment
          }
        }
      }
    }
  `;

  const allProducts: Product[] = [];
  let after: string | null = null;

  try {
    do {
      const data: ProductsResponse = await shopifyFetch<ProductsResponse>({
        query,
        variables: { first: 250, after },
        cache: 'no-store',
      });

      allProducts.push(
        ...data.products.edges
          .map((edge: { node: ShopifyProduct }) => reshapeProduct(edge.node))
          .filter((product: Product) => !isFeeProductHandle(product.handle))
      );

      after = data.products.pageInfo.hasNextPage ? data.products.pageInfo.endCursor : null;
    } while (after);

    return allProducts;
  } catch (error) {
    return withAdminCatalogFallback(error, 'getAllProducts', () => adminMemo('adminGetAllProducts', adminGetAllProducts), []);
  }
}

// cache() memoizes per request render pass, so generateMetadata and the page
// component share one lookup — without it they could disagree under load
// (page rendered the product while metadata said "Product Not Found").
export const getProduct = cache(async function getProduct(
  handle: string,
): Promise<Product | null> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query GetProduct($handle: String!) {
      product(handle: $handle) {
        ...ProductFragment
      }
    }
  `;

  try {
    const data = await shopifyFetch<ProductResponse>({
      query,
      variables: { handle },
      cache: 'no-store',
    });

    if (!data.product) return null;

    // Products with >100 variants (e.g. round-neck-t-shirt = 305) need pagination
    // so isOptionAvailable() can see every variant. Shopify's Storefront max is
    // 250 per page, so each follow-up fetch pulls the next 250.
    let pageInfo = data.product.variants.pageInfo;
    while (pageInfo?.hasNextPage && pageInfo.endCursor) {
      const moreQuery = `
        query GetMoreVariants($handle: String!, $cursor: String!) {
          product(handle: $handle) {
            variants(first: 250, after: $cursor) {
              pageInfo { hasNextPage endCursor }
              edges {
                node {
                  id
                  title
                  availableForSale
                  selectedOptions { name value }
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  image { url altText width height }
                }
              }
            }
          }
        }
      `;
      const more = await shopifyFetch<{
        product: { variants: ShopifyProduct['variants'] } | null;
      }>({
        query: moreQuery,
        variables: { handle, cursor: pageInfo.endCursor },
        cache: 'no-store',
      });
      if (!more.product) break;
      data.product.variants.edges.push(...more.product.variants.edges);
      pageInfo = more.product.variants.pageInfo;
    }

    return reshapeProduct(data.product);
  } catch (error) {
    return withAdminCatalogFallback(error, 'getProduct', () => adminMemo(`adminGetProduct:${handle}`, () => adminGetProduct(handle)));
  }
});

// When a product's handle changes in Shopify, Shopify automatically creates a
// URL redirect entry mapping the old path to the new one. Because we run a
// custom Next.js storefront (not the Shopify-hosted one), we have to query
// and apply those redirects ourselves. Used by the product/collection pages
// as a fallback when a handle lookup returns null.
export async function getUrlRedirect(path: string): Promise<string | null> {
  const query = `
    query GetRedirect($q: String!) {
      urlRedirects(first: 1, query: $q) {
        edges { node { path target } }
      }
    }
  `;
  try {
    const data = await shopifyFetch<{
      urlRedirects: { edges: Array<{ node: { path: string; target: string } }> };
    }>({
      query,
      variables: { q: `path:${path}` },
      cache: 'no-store',
    });
    const match = data.urlRedirects.edges.find((e) => e.node.path === path);
    return match ? match.node.target : null;
  } catch (error) {
    if (isShopifyAccessDeniedError(error)) return null;
    throw error;
  }
}

// Predictive Search API
export async function predictiveSearch(searchQuery: string, limit: number = 6): Promise<Product[]> {
  const query = `
    query PredictiveSearch($query: String!, $limit: Int!) {
      predictiveSearch(query: $query, limit: $limit, types: PRODUCT) {
        products {
          id
          handle
          title
          availableForSale
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 1) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions {
                  name
                  value
                }
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
          options {
            id
            name
            values
          }
          tags
          productType
          vendor
          description
          descriptionHtml
          images(first: 1) {
            edges {
              node {
                url
                altText
                width
                height
              }
            }
          }
          seo {
            title
            description
          }
        }
      }
    }
  `;

  try {
    const data = await shopifyFetch<{
      predictiveSearch: { products: ShopifyProduct[] };
    }>({
      query,
      variables: { query: searchQuery, limit },
      cache: 'no-store',
    });

    return data.predictiveSearch.products.map((p) => reshapeProduct(p));
  } catch (error) {
    return withAdminCatalogFallback(
      error,
      'predictiveSearch',
      () => adminSearchProducts(searchQuery, limit),
      [],
    );
  }
}

// Search API
export async function searchProducts(searchQuery: string, first: number = 50): Promise<Product[]> {
  const query = `
    ${PRODUCT_FRAGMENT}
    query SearchProducts($query: String!, $first: Int!) {
      search(query: $query, first: $first, types: PRODUCT) {
        edges {
          node {
            ... on Product {
              ...ProductFragment
            }
          }
        }
      }
    }
  `;

  try {
    const data = await shopifyFetch<{
      search: { edges: { node: ShopifyProduct }[] };
    }>({
      query,
      variables: { query: searchQuery, first },
      cache: 'no-store',
    });

    return data.search.edges.map((edge) => reshapeProduct(edge.node));
  } catch (error) {
    return withAdminCatalogFallback(
      error,
      'searchProducts',
      () => adminSearchProducts(searchQuery, first),
      [],
    );
  }
}

// Collection APIs
export const getCollections = cache(async function getCollections(): Promise<Collection[]> {
  const query = `
    ${COLLECTION_FRAGMENT}
    ${PRODUCT_FRAGMENT}
    query GetCollections {
      collections(first: 100) {
        edges {
          node {
            ...CollectionFragment
            products(first: 100) {
              edges {
                node {
                  ...ProductFragment
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const data = await shopifyFetch<CollectionsResponse>({
      query,
      cache: 'no-store',
    });

    return data.collections.edges.map((edge) => reshapeCollection(edge.node));
  } catch (error) {
    return withAdminCatalogFallback(error, 'getCollections', () => adminMemo('adminGetCollections', adminGetCollections), []);
  }
});

export const getCollection = cache(async function getCollection(
  handle: string,
): Promise<Collection | null> {
  const query = `
    ${COLLECTION_FRAGMENT}
    ${PRODUCT_FRAGMENT}
    query GetCollection($handle: String!) {
      collection(handle: $handle) {
        ...CollectionFragment
        products(first: 100) {
          edges {
            node {
              ...ProductFragment
            }
          }
        }
      }
    }
  `;

  try {
    const data = await shopifyFetch<CollectionResponse>({
      query,
      variables: { handle },
      cache: 'no-store',
    });

    return data.collection ? reshapeCollection(data.collection) : null;
  } catch (error) {
    return withAdminCatalogFallback(error, 'getCollection', () => adminMemo(`adminGetCollection:${handle}`, () => adminGetCollection(handle)));
  }
});

// Cart APIs
export async function createCart(): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation CreateCart {
      cartCreate {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartCreateResponse>({
    query,
    cache: 'no-store',
  });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  return enrichCart(reshapeCart(data.cartCreate.cart));
}

export async function getCart(cartId: string): Promise<Cart | null> {
  const query = `
    ${CART_FRAGMENT}
    query GetCart($cartId: ID!) {
      cart(id: $cartId) {
        ...CartFragment
      }
    }
  `;

  const data = await shopifyFetch<CartResponse>({
    query,
    variables: { cartId },
    cache: 'no-store',
  });

  return data.cart ? enrichCart(reshapeCart(data.cart)) : null;
}

export async function addToCart(
  cartId: string,
  lines: { merchandiseId: string; quantity: number; attributes?: { key: string; value: string }[] }[]
): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartLinesAddResponse>({
    query,
    variables: { cartId, lines },
    cache: 'no-store',
  });

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }

  return enrichCart(reshapeCart(data.cartLinesAdd.cart));
}

export async function updateCart(
  cartId: string,
  lines: { id: string; quantity: number }[]
): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation UpdateCart($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartLinesUpdateResponse>({
    query,
    variables: { cartId, lines },
    cache: 'no-store',
  });

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }

  return enrichCart(reshapeCart(data.cartLinesUpdate.cart));
}

export async function removeFromCart(cartId: string, lineIds: string[]): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation RemoveFromCart($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<CartLinesRemoveResponse>({
    query,
    variables: { cartId, lineIds },
    cache: 'no-store',
  });

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }

  return enrichCart(reshapeCart(data.cartLinesRemove.cart));
}

// Update cart buyer identity (email, address, etc.)
export async function updateCartBuyerIdentity(
  cartId: string,
  buyerIdentity: {
    email?: string;
    phone?: string;
    countryCode?: string;
    deliveryAddressPreferences?: {
      deliveryAddress: {
        address1?: string;
        address2?: string;
        city?: string;
        company?: string;
        country?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        province?: string;
        zip?: string;
      };
    }[];
  }
): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
      cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    cartBuyerIdentityUpdate: {
      cart: ShopifyCart;
      userErrors: { field: string[]; message: string }[];
    };
  }>({
    query,
    variables: { cartId, buyerIdentity },
    cache: 'no-store',
  });

  if (data.cartBuyerIdentityUpdate.userErrors.length > 0) {
    throw new Error(data.cartBuyerIdentityUpdate.userErrors[0].message);
  }

  return enrichCart(reshapeCart(data.cartBuyerIdentityUpdate.cart));
}

// Apply discount code to cart
export async function applyDiscountCode(
  cartId: string,
  discountCodes: string[]
): Promise<{ cart: Cart; userErrors: string[] }> {
  const query = `
    ${CART_FRAGMENT}
    mutation CartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          ...CartFragment
          discountCodes {
            code
            applicable
          }
          discountAllocations {
            discountedAmount {
              amount
              currencyCode
            }
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    cartDiscountCodesUpdate: {
      cart: ShopifyCart & {
        discountCodes: { code: string; applicable: boolean }[];
        discountAllocations: { discountedAmount: Money }[];
      };
      userErrors: { field: string[]; message: string }[];
    };
  }>({
    query,
    variables: { cartId, discountCodes },
    cache: 'no-store',
  });

  const userErrors = data.cartDiscountCodesUpdate.userErrors.map(e => e.message);

  return {
    cart: await enrichCart(reshapeCart(data.cartDiscountCodesUpdate.cart)),
    userErrors,
  };
}

// Remove discount codes from cart
export async function removeDiscountCodes(cartId: string): Promise<Cart> {
  const query = `
    ${CART_FRAGMENT}
    mutation CartDiscountCodesUpdate($cartId: ID!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: []) {
        cart {
          ...CartFragment
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await shopifyFetch<{
    cartDiscountCodesUpdate: {
      cart: ShopifyCart;
      userErrors: { field: string[]; message: string }[];
    };
  }>({
    query,
    variables: { cartId },
    cache: 'no-store',
  });

  return enrichCart(reshapeCart(data.cartDiscountCodesUpdate.cart));
}

// Utility function to format price
export function formatPrice(money: Money): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: money.currencyCode,
  }).format(parseFloat(money.amount));
}

// Article APIs (Shopify blog posts — used alongside the static posts.json)
const ARTICLE_FRAGMENT = `
  fragment ArticleFragment on Article {
    handle
    title
    excerpt
    excerptHtml
    contentHtml
    publishedAt
    image {
      url
      altText
      width
      height
    }
    seo {
      title
      description
    }
  }
`;

export async function getShopifyArticles(): Promise<ShopifyArticle[]> {
  const query = `
    ${ARTICLE_FRAGMENT}
    query GetArticles {
      blogs(first: 10) {
        edges {
          node {
            handle
            articles(first: 250, sortKey: PUBLISHED_AT, reverse: true) {
              edges {
                node {
                  ...ArticleFragment
                }
              }
            }
          }
        }
      }
    }
  `;

  try {
    const data = await shopifyFetch<ArticlesResponse>({
      query,
      tags: ['articles'],
    });

    return data.blogs.edges.flatMap((blog) =>
      blog.node.articles.edges.map((edge) => edge.node),
    );
  } catch (error) {
    if (isShopifyAccessDeniedError(error)) {
      return withAdminCatalogFallback(
        error,
        'getShopifyArticles',
        adminGetShopifyArticles,
        [],
      );
    }
    console.error('Failed to fetch Shopify articles:', error);
    return [];
  }
}
